// @ts-check

import axios from "axios";
import { createContext, useContext, useMemo, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";

const DashboardContext = /** @type {import("react").Context<import("../types/global").DashboardContextValue | undefined>} */ (createContext(undefined));

export function useDashboard() {
  const context = useContext(DashboardContext);
  if (!context) throw new Error("useDashboard must be used within a DashboardProvider");
  return context;
}

export default function DashboardProvider(/** @type {import("react").PropsWithChildren<{}>} */ { children }) {
  const locale = localStorage.getItem("locale") || "en";
  const navigate = useNavigate();

  const dashboardUrl = locale === "en" ? process.env.REACT_APP_DASHBOARD_EN : process.env.REACT_APP_DASHBOARD_TR;

  const hostname = window.location.hostname.toLowerCase();
  const parts = hostname.split('.');

  const isGrungardTenant = parts.some((part, i) =>
    part === 'grungard' &&
    (parts[i + 1] === 'grungard' || parts[i + 1] === 'akillisula')
  );

  useEffect(() => {
    if (isGrungardTenant) {
      navigate('/control-center/gardens');
    }
  }, [isGrungardTenant]);

  const { data: dashboardData, isFetching: isFetchingDashboard } = useQuery(
    ["Dashboard"],
    /** @returns {Promise<import("axios").AxiosResponse<import("../types/dashboard").GetDashboardUrlResponse>>} */
    () => axios.get(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/dashboard/${dashboardUrl}`)
  );

  const { data: wateringDashboard, isFetching: isFetchingWateringDashboard } = useQuery(
    ["WateringDashboard"],
    () =>
      axios.post(process.env.REACT_APP_PLANNER_API_END_POINT + "/garden/dashboard/", {
        id: process.env.REACT_APP_TENANT_WATERING_DASHBOARD,
        params: {
          relative_date: "yesterday",
          relative_date_1: "past7days",
          relative_date_2: "past30days",
        },
      }),
    { refetchOnWindowFocus: false }
  );

  const dashboardContextValue = useMemo(() => {
    console.count("dashboard context value changed");
    /** @type {import("../types/dashboard").DashboardContextValue} */
    const value = {
      dashboardUrl: dashboardData?.data?.url || null,
      isFetchingDashboard,
      isFetchingWateringDashboard,
      wateringDashboardUrl: wateringDashboard?.data?.url || null,
    };

    return value;
  }, [dashboardData?.data?.url, isFetchingDashboard, isFetchingWateringDashboard, wateringDashboard?.data?.url]);

  return <DashboardContext.Provider value={dashboardContextValue}>{children}</DashboardContext.Provider>;
}

DashboardProvider.DashboardIFrame = function DashboardIFrame() {
  const { dashboardUrl, isFetchingDashboard } = useDashboard();
  const [iframeHeight, setIframeHeight] = useState(800);

  useEffect(() => {
    const controller = new AbortController();

    window.addEventListener(
      "message",
      (event) => {
        if (event.data?.metabase?.type === "frame" && event.data?.metabase?.frame?.mode === "fit") {
          setIframeHeight(event.data.metabase.frame.height);
        }
      },
      { signal: controller.signal }
    );

    return () => controller.abort();
  }, []);

  if (isFetchingDashboard) return null;
  if (!dashboardUrl) return null;

  return <iframe src={dashboardUrl} title="Dashboard" style={{ width: "100%", height: iframeHeight, border: "none" }} />;
};
